import React from "react"
import PropTypes from "prop-types"
import MediaQuery from "react-responsive"
import Gesture from "rc-gesture"

// Utils
import { isTouch, getClosestMatch } from "lib/utils/helpers"

// Configs
import ModalConfig from "components/Stacker/config/ModalConfig"
import BreakPointConfig from "components/Stacker/constants/breakpoints"

// Actions
import { toggleModal } from "components/Stacker/actions/ModalActions"
import { zoomIn, zoomOut } from "components/Stacker/actions/ZoomModalActions"

// Components
import Icon from "components/Stacker/components/Icon/Icon"
import Loader from "components/Stacker/components/Loader/Loader"

import "./ProductImage.scss"
import shadowDOM from "components/Stacker/utils/shadowDOM"
import { useState, useEffect } from "react"
import { useRef } from "react"
import { useDispatch } from "react-redux"

function ProductImage({ isZoomed, isMobileScreen, scale, image, altImage }) {
  const [pos, setPosition] = useState({ x: 0, y: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const imgRef = useRef()
  const dispatch = useDispatch()

  useEffect(() => {
    const preventNativeGestures = (e) => {
      if (e.scale !== 1) {
        e.preventDefault()
      }
    }

    document
      .getElementsByName("viewport")[0]
      .setAttribute(
        "content",
        "width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=no"
      )
    shadowDOM.addEventListener("touchmove", preventNativeGestures, {
      passive: false,
    })

    return () => {
      document
        .getElementsByName("viewport")[0]
        .setAttribute("content", "width=device-width, initial-scale=1")
      shadowDOM.removeEventListener("touchmove", preventNativeGestures, {
        passive: false,
      })
    }
  }, [])

  const handleImageLoaded = () => {
    const x = (window.innerWidth - imgRef.current.width) / 2
    const y = (window.innerHeight - imgRef.current.height) / 2

    if (isMobileScreen) {
      setPosition({ x, y })

      if (isZoomed) {
        centerImage()
      } else {
        imgRef.current.setAttribute(
          "style",
          `transform: translate(0, 0) scale(${scale});`
        )
      }
    } else if (isTouch()) {
      centerImage()
    } else {
      setPosition({ x: pos.x, y })
      imgRef.current.setAttribute("style", `transform: translate(0, ${y}px)`)
    }
    setIsLoading(false)
  }

  const centerImage = () => {
    const isLandscape = window.innerHeight < window.innerWidth
    const x = (window.innerWidth - imgRef.current.width) / 2
    let y = (window.innerHeight - imgRef.current.height) / 2

    if (isLandscape) {
      y = (window.innerHeight - window.innerWidth) / 2
      imgRef.current.setAttribute(
        "style",
        `transform: translate(0, ${y}px); width: 100%; height: auto;`
      )
    } else {
      imgRef.current.setAttribute("style", `transform: translate(${x}px, 0);`)
    }
  }

  const closeZoomView = (e) => {
    dispatch(toggleModal(ModalConfig.MODAL_PDP_ZOOM, false))
  }

  const touchMoveImage = (e) => {
    // changed this function to only allow horizontal movement for mobile
    const { x, y } = pos
    const isLandscape = window.innerHeight < window.innerWidth
    if (e.touches.length > 1) {
      return
    }
    const heightScale =
      imgRef.current.naturalWidth > window.screen.availWidth
        ? window.screen.availWidth / imgRef.current.naturalWidth
        : imgRef.current.naturalWidth / window.screen.availWidth
    const imgHeight = imgRef.current.naturalHeight * heightScale
    let xPos =
      x +
      (e.moveStatus.x *
        (imgRef.current.scrollWidth / window.screen.availWidth)) /
        5
    let yPos =
      y + (e.moveStatus.y * (imgHeight / window.screen.availHeight)) / 5

    // These set scroll limits at each edge of the image
    if (xPos > 0) {
      xPos = 0
    }
    if (xPos < 0 - (imgRef.current.scrollWidth - window.screen.availWidth)) {
      xPos = 0 - (imgRef.current.scrollWidth - window.screen.availWidth)
    }
    if (yPos > 0) {
      yPos = 0
    }
    if (yPos < window.innerHeight - imgHeight) {
      yPos = window.innerHeight - imgHeight
    }

    if (
      getClosestMatch(imgRef.current, ".slick-active") &&
      !isLandscape &&
      isZoomed
    ) {
      // if the current slide is active:
      setPosition({
        x: xPos,
        y: yPos,
      })
      imgRef.current.setAttribute(
        "style",
        `transition: all 0.25s ease-out; transform: translateX(${xPos}px);`
      )
    } else if (
      getClosestMatch(imgRef.current, ".slick-active") &&
      isLandscape
    ) {
      const newScale = window.innerWidth / window.innerHeight
      xPos = (window.innerWidth - imgRef.current.scrollWidth) / 2
      setPosition({
        x: xPos,
        y: yPos,
      })
      imgRef.current.setAttribute(
        "style",
        `transition: all 0.25s ease-out; transform: translate(${xPos}px,${yPos}px) scale(${newScale});`
      )
    }
  }

  const zoomIn = (e) => {
    const newScale = 1

    if (!isZoomed) {
      const x = (window.innerWidth - imgRef.current.scrollWidth) / 2
      imgRef.current.setAttribute(
        "style",
        `transform: translate(${x}px,0) scale(${newScale});`
      )
      setPosition({ x, y: pos.y })
      dispatch(zoomIn(newScale))
    }
  }

  const zoomOut = (e) => {
    const newScale = window.innerWidth / window.innerHeight

    if (isZoomed) {
      const x = (window.innerWidth - imgRef.current.scrollWidth) / 2
      imgRef.current.setAttribute(
        "style",
        `transform: translate(${x}px,0) scale(${newScale});`
      )
      setPosition({
        x,
        y: pos.y,
      })
      dispatch(zoomOut(newScale))
    }
  }

  const followMouseVertical = (e) => {
    const { height } = imgRef.current
    const yPos = e.clientY
    const yPercent = yPos / window.innerHeight
    let scroll = Math.floor((yPercent * height) / 2)
    if (scroll > height - window.innerHeight) {
      scroll = height - window.innerHeight
    }
    // imgRef.current.scrollTo(0, yPercent * height);
    imgRef.current.setAttribute("style", `transform: translateY(-${scroll}px);`)
  }

  const isLandscape = window.innerHeight < window.innerWidth

  return (
    <button
      className="ProductImage"
      onClick={!isMobileScreen && !isTouch() ? closeZoomView : null}
      tabIndex={0}
    >
      {isLoading && <Loader />}
      <Gesture
        onPinchIn={!isLandscape ? zoomOut : zoomIn}
        onPinchOut={!isLandscape ? zoomIn : zoomOut}
        onPanMove={touchMoveImage}
        enableSwipe
        enablePinch
      >
        {/* this div is necessary to prevent the Gesture component from overriding the inline styles on iOS devices */}
        <div>
          <img
            src={image}
            id={image}
            alt={altImage}
            ref={imgRef}
            onLoad={handleImageLoaded}
            onMouseMove={!isMobileScreen ? followMouseVertical : null}
          />
        </div>
      </Gesture>
      <MediaQuery query={BreakPointConfig.IS_MOBILE}>
        {!isTouch() && (
          <div className="zoom-buttons-container">
            <button className="transparent zoom-in" onClick={zoomIn}>
              <Icon iconName="icon-zoom-in-mobile" />
            </button>
            <button className="transparent zoom-out" onClick={zoomOut}>
              <Icon iconName="icon-zoom-out-mobile" />
            </button>
          </div>
        )}
      </MediaQuery>
    </button>
  )
}

ProductImage.propTypes = {
  image: PropTypes.string,
  altImage: PropTypes.string,
  isMobileScreen: PropTypes.bool,
  isZoomed: PropTypes.bool,
  scale: PropTypes.number,
}

export default ProductImage
